import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Dropdown, Spinner } from 'react-bootstrap';
import { chatAPI, ChatThread, loadChatLang, previewText, vedSourcerChatAPI, VedSourcerChatThread } from '../../services/chatApi';
import { getChatSocket } from '../../services/chatSocket';
import { useChatUi } from '../../context/ChatUiContext';

function chatPageUrl(productId?: string) {
  const base = `${window.location.origin}/chat`;
  return productId ? `${base}?productId=${encodeURIComponent(productId)}` : base;
}

const ChatBell: React.FC = () => {
  const { openModal } = useChatUi();
  const [unread, setUnread] = useState(0);
  const [inbox, setInbox] = useState<ChatThread[]>([]);
  const [buyerInbox, setBuyerInbox] = useState<VedSourcerChatThread[]>([]);
  const [loading, setLoading] = useState(false);
  const lang = loadChatLang();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [countRes, threadsRes, buyerRes]: any[] = await Promise.all([
        chatAPI.getUnreadCount(),
        chatAPI.getThreads({ limit: 50 }),
        vedSourcerChatAPI.getThreads().catch(() => ({ threads: [] })),
      ]);
      const buyerThreads: VedSourcerChatThread[] = (buyerRes.threads || []).filter(
        (th: VedSourcerChatThread) => (Number(th.unread_count) || 0) > 0 || (Number(th.mention_unread_count) || 0) > 0
      );
      setUnread((countRes.count ?? 0) + buyerThreads.reduce((sum, th) => sum + (Number(th.unread_count) || 0), 0));
      const threads: ChatThread[] = threadsRes.threads || [];
      setInbox(threads.filter((t) => t.has_unread).slice(0, 10));
      setBuyerInbox(buyerThreads.slice(0, 10));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const socket = getChatSocket();
    const onUnread = () => refresh();
    socket.on('chat:unread', onUnread);
    socket.on('chat:message', onUnread);
    const id = window.setInterval(refresh, 30000);
    return () => {
      socket.off('chat:unread', onUnread);
      socket.off('chat:message', onUnread);
      window.clearInterval(id);
    };
  }, [refresh]);

  return (
    <Dropdown align="end" onToggle={(open) => open && refresh()}>
      <Dropdown.Toggle
        variant="outline-light"
        size="sm"
        id="chat-bell-toggle"
        className="d-flex align-items-center position-relative me-2"
        style={{ border: 'none' }}
      >
        <span style={{ fontSize: 18 }} title="Chat">💬</span>
        {unread > 0 ? (
          <Badge
            bg="danger"
            pill
            className="position-absolute"
            style={{ top: 0, right: 0, fontSize: 10 }}
          >
            {unread > 99 ? '99+' : unread}
          </Badge>
        ) : null}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: 300, maxHeight: 420, overflow: 'auto' }}>
        <div className="px-3 py-2 d-flex gap-2 border-bottom">
          <Button
            size="sm"
            variant="primary"
            className="flex-grow-1"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openModal();
            }}
          >
            Открыть здесь
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            className="flex-grow-1"
            as="a"
            href={chatPageUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onMouseDown={(e) => e.stopPropagation()}
          >
            Новая вкладка
          </Button>
        </div>
        <Dropdown.Header>Непрочитанные</Dropdown.Header>
        {loading && (
          <div className="text-center py-2">
            <Spinner size="sm" />
          </div>
        )}
        {!loading && inbox.length === 0 && buyerInbox.length === 0 && (
          <div className="px-3 py-2 small text-muted">Нет непрочитанных</div>
        )}
        {buyerInbox.map((th) => (
          <Dropdown.Item
            key={`buyer-${th.order_id}`}
            onMouseDown={(e) => {
              e.preventDefault();
              openModal({ tab: 'buyers', orderId: th.order_id });
            }}
          >
            <div className="fw-semibold">
              {(Number(th.mention_unread_count) || 0) > 0 ? '@ ' : ''}
              Buyers · {th.internal_article || `#${th.order_id}`}
            </div>
            <div className="small text-muted text-truncate">{th.last_body || '—'}</div>
          </Dropdown.Item>
        ))}
        {inbox.map((t) => (
          <Dropdown.Item
            key={t.product_id}
            onMouseDown={(e) => {
              e.preventDefault();
              openModal(String(t.product_id));
            }}
          >
            <div className="fw-semibold">{t.article || t.product_id}</div>
            <div className="small text-muted text-truncate">{previewText(t, lang)}</div>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ChatBell;
